import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";

import PanelTemplate from "../../templates/Panel";
import Box from "../../components/Box";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import BrandBreadcrumb from "./BrandBreadcrumb";
import Input from "../../components/Input";
import InputEditor from "../../components/InputEditor";
import InputTextarea from "../../components/InputTextarea";
import InputCheckboxes from "../../components/InputCheckboxes";
import InputCheckboxesSublevels from "../../components/InputCheckboxesSublevels";
import PageFooter from "../../components/PageFooter";
import Modal from "../../components/Modal";
import { Loading } from "../../components/Loading";
import ProductImagesTab from "../Product/tabs/ImagesTab";
import SupplierProductPicker from "./SupplierProductPicker";

import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { createOptions } from "../../helpers/selects";
import { convertIntToMoney } from "../../helpers/conversions";
import { canEditBrands } from "../../services/auth";
import { FIELD_LABELS, UNAVAILABLE_REASONS } from "../../helpers/personalizedProduct";
import useNavigationBlocker from "../../hooks/useNavigationBlocker";

const MAIN_FIELDS = ['images', 'name', 'description', 'tech_description'];
const MORE_FIELDS = ['categories', 'seo', 'min_quantity', 'customizations'];

const htmlToText = html => {
    const decoder = document.createElement('textarea');
    decoder.innerHTML = (html ?? '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
        .replace(/<[^>]*>/g, '');
    return decoder.value.replace(/[ \t]+/g, ' ').replace(/\s*\n\s*/g, '\n').trim();
};
const formatQuantity = quantity => Number(quantity).toLocaleString('pt-br');
const parseKeywords = text => text.split(',').map(keyword => keyword.trim()).filter(Boolean);

// O InputEditor reescreve o HTML ao montar (<p> vira <div>, <b> vira <strong>);
// sem passar os dois lados pelo mesmo conversor, a tela abriria "alterada".
const normalizeHtml = html => stateToHTML(stateFromHTML(html ?? ''), { defaultBlockTag: 'div' });

// O backend ignora o valor de campo herdado, então ele não conta como alteração.
const formSnapshot = ({ inherited, fields, keywordsText, subproductImages, status }) => {
    const custom = field => !inherited.includes(field);

    return JSON.stringify({
        status,
        inherited: [...inherited].sort(),
        name: custom('name') ? fields.name : null,
        description: custom('description') ? normalizeHtml(fields.description) : null,
        tech_description: custom('tech_description') ? normalizeHtml(fields.tech_description) : null,
        seo: custom('seo') ? [fields.meta_title, fields.meta_description, parseKeywords(keywordsText)] : null,
        min_quantity: custom('min_quantity') ? String(fields.min_quantity) : null,
        categories: custom('categories') ? [...fields.categories].sort() : null,
        customizations: custom('customizations') ? [...fields.customizations].sort() : null,
        subproduct_images: custom('images') ? subproductImages : null
    });
};

const FieldRow = ({ field, inherited, onToggle, disabled, original, children }) => (
    <div className={`personalized-field ${inherited ? 'inherited' : 'custom'}`}>
        <div className="personalized-field-head">
            <span className="personalized-field-label">{FIELD_LABELS[field]}</span>
            <div className="brand-segmented small">
                <button type="button" className={inherited ? 'active' : ''} disabled={disabled} onClick={() => onToggle(field, true)}>
                    Igual ao original
                </button>
                <button type="button" className={!inherited ? 'active' : ''} disabled={disabled} onClick={() => onToggle(field, false)}>
                    Personalizado
                </button>
            </div>
        </div>
        {inherited
            ? <div className="personalized-field-original">{original}</div>
            : <div className="personalized-field-editor">{children}</div>
        }
    </div>
);

export const PersonalizedProduct = () => {
    const { id, productId } = useParams();
    const [searchParams] = useSearchParams();
    const canEdit = canEditBrands();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState(null);

    const [inherited, setInherited] = useState([]);
    const [fields, setFields] = useState({});
    const [keywordsText, setKeywordsText] = useState('');
    const [status, setStatus] = useState(false);
    const [product, setProduct] = useState({ id: productId, images: [] });
    const [subproductImages, setSubproductImages] = useState({});
    const [showMore, setShowMore] = useState(false);
    const [savedSnapshot, setSavedSnapshot] = useState(null);
    // Remonta os InputEditor, que só leem o value ao montar.
    const [formVersion, setFormVersion] = useState(0);

    const [categoryOptions, setCategoryOptions] = useState([]);
    const [customizationOptions, setCustomizationOptions] = useState([]);

    const [showRelink, setShowRelink] = useState(false);
    const [relinking, setRelinking] = useState(null);

    useEffect(() => {
        getProduct();
        getCategories();
        getCustomizations();
    }, [productId]);

    const resetForm = resource => {
        const form = {
            inherited: resource.inherited_fields ?? [],
            fields: {
                name: resource.fields.name ?? '',
                description: resource.fields.description ?? '',
                tech_description: resource.fields.tech_description ?? '',
                meta_title: resource.fields.meta_title ?? '',
                meta_description: resource.fields.meta_description ?? '',
                min_quantity: resource.fields.min_quantity ?? '',
                categories: resource.fields.categories.map(category => category.id),
                customizations: resource.fields.customizations.map(customization => customization.id)
            },
            keywordsText: (resource.fields.keywords ?? []).join(', '),
            subproductImages: Object.fromEntries(resource.subproducts.map(subproduct => [subproduct.id, subproduct.product_image_id])),
            status: Boolean(resource.status)
        };

        setInherited(form.inherited);
        setFields(form.fields);
        setKeywordsText(form.keywordsText);
        setSubproductImages(form.subproductImages);
        setStatus(form.status);
        setSavedSnapshot(formSnapshot(form));
        setFormVersion(prev => prev + 1);

        if (MORE_FIELDS.some(field => !form.inherited.includes(field))) setShowMore(true);
    };

    const applyPayload = resource => {
        setData(resource);
        setProduct({ id: resource.id, images: resource.images });
        resetForm(resource);
    };

    const getProduct = () => {
        setLoading(true);
        api.get(`/brand/${id}/personalized/${productId}`).then(res => {
            applyPayload(res.data.resource);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    };

    const getCategories = () => {
        api.get(`/category`).then(res => {
            setCategoryOptions(res.data.resources.map(category => ({
                label: category.name,
                value: category.id,
                options: category.categories.map(subcategory => ({ label: subcategory.name, value: subcategory.id }))
            })));
        });
    };

    const getCustomizations = () => {
        api.get(`/customization`).then(res => {
            setCustomizationOptions(createOptions(res.data.resources, 'name', 'id'));
        });
    };

    const isInherited = field => inherited.includes(field);

    const toggleField = (field, toInherited) => {
        setInherited(prev => toInherited
            ? [...new Set([...prev, field])]
            : prev.filter(item => item !== field));
    };

    // Os checkboxes do sistema chamam setSelecteds com função (prev => ...).
    const setField = (key, value) => setFields(prev => ({
        ...prev,
        [key]: typeof value === 'function' ? value(prev[key]) : value
    }));

    const dirty = useMemo(
        () => savedSnapshot !== null && formSnapshot({ inherited, fields, keywordsText, subproductImages, status }) !== savedSnapshot,
        [savedSnapshot, inherited, fields, keywordsText, subproductImages, status]
    );
    const navigationBlocker = useNavigationBlocker(dirty);

    const save = () => {
        const wasOnSite = Boolean(data.status);
        setSaving(true);

        api.put(`/brand/${id}/personalized/${productId}`, {
            inherited_fields: inherited,
            name: fields.name,
            description: fields.description,
            tech_description: fields.tech_description,
            meta_title: fields.meta_title,
            meta_description: fields.meta_description,
            keywords: parseKeywords(keywordsText),
            min_quantity: fields.min_quantity === '' ? undefined : Number(fields.min_quantity),
            categories: fields.categories,
            customizations: fields.customizations,
            subproduct_images: subproductImages,
            status
        }).then(res => {
            applyPayload(res.data.resource);
            const siteName = `site da ${data.brand.name}`;
            if (status && !wasOnSite) toast.success(`Publicado no ${siteName}`);
            else if (!status && wasOnSite) toast.success(`Tirado do ${siteName}`);
            else if (status) toast.success(`Alterações salvas e já no ${siteName}`);
            else toast.success('Alterações salvas');
        }).catch(error => {
            renderError(error);
        }).then(() => setSaving(false));
    };

    const relink = source => {
        setRelinking(source.id);
        api.put(`/brand/${id}/personalized/${productId}/source`, { product_id: source.id }).then(res => {
            applyPayload(res.data.resource);
            setShowRelink(false);
            toast.success('Vínculo trocado');
        }).catch(error => {
            renderError(error);
        }).then(() => setRelinking(null));
    };

    if (loading || !data) {
        return (
            <PanelTemplate id="brand-personalized">
                <Loading show={true} />
            </PanelTemplate>
        );
    }

    const original = data.original;
    const previewImage = (isInherited('images') ? original?.images : product.images)?.[0];
    const previewTitle = isInherited('name') ? original?.fields.name : fields.name;
    const totalStock = data.subproducts.reduce((sum, subproduct) => sum + Number(subproduct.stock ?? 0), 0);
    // Quem chegou pela lista de Produtos volta para ela, com os mesmos filtros.
    const returnTo = searchParams.get('return_to');
    const fromProductList = Boolean(returnTo?.startsWith('/product/list'));
    const backTo = fromProductList ? returnTo : `/brand/edit/${id}?tab=products`;
    const saveLabel = status === Boolean(data.status)
        ? 'Salvar'
        : (status ? 'Salvar e publicar' : 'Salvar e tirar do site');

    const renderOriginalImages = () => (
        <div className="personalized-thumbs">
            {(original?.images ?? []).slice(0, 6).map(image => (
                <img key={image.id} src={image.image_url?.small} alt="" />
            ))}
            <span>{original?.images.length ?? 0} fotos do fornecedor</span>
        </div>
    );

    const renderField = field => {
        const originalFields = original?.fields ?? {};

        switch (field) {
            case 'images': return (
                <FieldRow key={field} field={field} inherited={isInherited(field)} onToggle={toggleField} disabled={!canEdit} original={renderOriginalImages()}>
                    <ProductImagesTab key={product.images.map(image => image.id).join()} product={product} setProduct={setProduct} />
                    {data.subproducts.length > 1 && product.images.length > 0 &&
                        <div className="personalized-color-images">
                            <p className="section-hint">Foto de cada cor</p>
                            {data.subproducts.map(subproduct => (
                                <div className="personalized-color-row" key={subproduct.id}>
                                    <span className="personalized-color-name">
                                        <i style={{ background: subproduct.color?.hexadecimal ?? '#ccc' }} />
                                        {subproduct.color?.name ?? 'Sem cor'}
                                    </span>
                                    <div className="personalized-color-options">
                                        {product.images.map(image => (
                                            <button
                                                type="button"
                                                key={image.id}
                                                className={subproductImages[subproduct.id] === image.id ? 'active' : ''}
                                                onClick={() => setSubproductImages(prev => ({ ...prev, [subproduct.id]: image.id }))}
                                            >
                                                <img src={image.image_url?.small} alt="" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    }
                </FieldRow>
            );
            case 'name': return (
                <FieldRow key={field} field={field} inherited={isInherited(field)} onToggle={toggleField} disabled={!canEdit} original={originalFields.name}>
                    <Input label="Título" value={fields.name} change={value => setField('name', value)} />
                </FieldRow>
            );
            case 'description':
            case 'tech_description': return (
                <FieldRow key={field} field={field} inherited={isInherited(field)} onToggle={toggleField} disabled={!canEdit} original={<span className="personalized-clamp">{htmlToText(originalFields[field]) || '—'}</span>}>
                    <InputEditor key={formVersion} value={fields[field] ?? ''} change={value => setField(field, value)} />
                </FieldRow>
            );
            case 'categories': return (
                <FieldRow key={field} field={field} inherited={isInherited(field)} onToggle={toggleField} disabled={!canEdit} original={(originalFields.categories ?? []).map(category => category.name).join(', ') || 'Nenhuma'}>
                    <InputCheckboxesSublevels options={categoryOptions} selecteds={fields.categories} setSelecteds={value => setField('categories', value)} />
                </FieldRow>
            );
            case 'seo': return (
                <FieldRow key={field} field={field} inherited={isInherited(field)} onToggle={toggleField} disabled={!canEdit} original={originalFields.meta_title || '—'}>
                    <Input label="Meta title" value={fields.meta_title} change={value => setField('meta_title', value)} />
                    <InputTextarea label="Meta description" rows={3} value={fields.meta_description} change={value => setField('meta_description', value)} />
                    <Input label="Palavras-chave (separadas por vírgula)" value={keywordsText} change={setKeywordsText} />
                </FieldRow>
            );
            case 'min_quantity': return (
                <FieldRow key={field} field={field} inherited={isInherited(field)} onToggle={toggleField} disabled={!canEdit} original={originalFields.min_quantity ?? '—'}>
                    <Input label="Quantidade mínima" type="number" value={fields.min_quantity} change={value => setField('min_quantity', value)} />
                </FieldRow>
            );
            case 'customizations': return (
                <FieldRow key={field} field={field} inherited={isInherited(field)} onToggle={toggleField} disabled={!canEdit} original={(originalFields.customizations ?? []).map(customization => customization.name).join(', ') || 'Nenhuma'}>
                    <InputCheckboxes options={customizationOptions} selecteds={fields.customizations} setSelecteds={value => setField('customizations', value)} />
                </FieldRow>
            );
            default: return null;
        }
    };

    return (
        <PanelTemplate id="brand-personalized">
            <SupplierProductPicker
                show={showRelink}
                close={() => setShowRelink(false)}
                brandId={id}
                title="Trocar o produto original"
                actionText="Usar este"
                picking={relinking}
                onPick={relink}
            />

            <BrandBreadcrumb items={fromProductList
                ? [{ label: 'Produtos', to: backTo }, { label: 'Personalizar produto' }]
                : [{ label: 'Marcas', to: '/brand/list' }, { label: data.brand.name, to: backTo }, { label: 'Personalizar produto' }]
            } />
            <h1 className="brand-page-title">Personalizar para {data.brand.name}</h1>
            <p className="personalized-source-line">
                <Icon name="link" size={14} />
                {original
                    ? <>Original: {original.name} · {original.supplier} {original.company_sku} · {original.colors} {original.colors === 1 ? 'cor' : 'cores'} · estoque {Number(original.stock ?? 0).toLocaleString('pt-br')}</>
                    : 'Sem produto original'}
                {original && !original.deleted &&
                    <a href={`/product/edit/${original.id}`} target="_blank" rel="noreferrer">
                        Abrir original <Icon name="external-link" size={12} />
                    </a>
                }
                {canEdit && <button type="button" onClick={() => setShowRelink(true)}>Trocar</button>}
            </p>

            {data.unavailable_reason &&
                <div className="personalized-alert">
                    <Icon name="alert" size={18} />
                    <span>{UNAVAILABLE_REASONS[data.unavailable_reason]}</span>
                    {canEdit &&
                        <Button type="secondary" size="small" text="Trocar vínculo" action={() => setShowRelink(true)} />
                    }
                </div>
            }

            <div className="personalized-layout">
                <aside className="personalized-preview">
                    <p className="personalized-preview-label">Prévia no site</p>
                    <div className="personalized-preview-card">
                        <div className="personalized-preview-image">
                            {previewImage?.image_url?.medium
                                ? <img src={previewImage.image_url.medium} alt={previewTitle} />
                                : <Icon name="image" size={32} />}
                        </div>
                        <strong>{previewTitle}</strong>
                        <div className="personalized-preview-colors">
                            {data.subproducts.filter(subproduct => subproduct.color).map(subproduct => (
                                <i key={subproduct.id} title={subproduct.color.name} style={{ background: subproduct.color.hexadecimal ?? '#ccc' }} />
                            ))}
                        </div>
                        <span>Estoque {totalStock.toLocaleString('pt-br')}</span>
                    </div>
                    <p className="personalized-preview-hint">
                        <Icon name="refresh" size={13} /> Preço, estoque e cores vêm do fornecedor e atualizam sozinhos.
                    </p>

                    <div className="personalized-visibility">
                        <span className="personalized-field-label">Visibilidade</span>
                        <div className="brand-mode-options" role="radiogroup">
                            {[
                                { value: true, label: 'No site', hint: `Aparece na vitrine da ${data.brand.name}.` },
                                { value: false, label: 'Fora do site', hint: 'Fica salvo aqui, mas não aparece na vitrine.' }
                            ].map(option => (
                                <button
                                    key={String(option.value)}
                                    type="button"
                                    role="radio"
                                    aria-checked={status === option.value}
                                    className={status === option.value ? 'active' : ''}
                                    disabled={!canEdit}
                                    onClick={() => setStatus(option.value)}
                                >
                                    <span className="brand-mode-dot" />
                                    <span>
                                        <strong>{option.label}</strong>
                                        <small>{option.hint}</small>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                </aside>

                <Box className="personalized-form">
                    <h2 className="section-title">O que muda nesta versão?</h2>
                    <p className="section-hint">O que ficar igual ao original acompanha as mudanças dele.</p>

                    {MAIN_FIELDS.map(renderField)}

                    <button type="button" className="personalized-more" onClick={() => setShowMore(prev => !prev)}>
                        <Icon name="arrow-dropdown" size={16} className={showMore ? 'open' : ''} />
                        {showMore
                            ? 'Menos campos'
                            : `Mais campos: categorias, SEO, quantidade mínima e gravações (${MORE_FIELDS.every(isInherited) ? 'todos iguais ao original' : 'alguns personalizados'})`}
                    </button>
                    {showMore && MORE_FIELDS.map(renderField)}

                    <div className="personalized-price">
                        <span className="personalized-field-label">Preço</span>
                        <p className="section-hint">
                            O custo vem do fornecedor, em todas as faixas. A sua regra de preço vale por cima, como em qualquer produto.
                        </p>
                        {data.price_preview.length > 0 &&
                            <table className="personalized-price-table">
                                <thead>
                                    <tr><th>Faixa</th><th>Custo do fornecedor</th></tr>
                                </thead>
                                <tbody>
                                    {data.price_preview.map(range => (
                                        <tr key={range.init_quantity}>
                                            <td>{range.final_quantity >= 9999999
                                                ? `${formatQuantity(range.init_quantity)} ou mais`
                                                : `${formatQuantity(range.init_quantity)} a ${formatQuantity(range.final_quantity)}`}</td>
                                            <td>{convertIntToMoney(range.source_price, true)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        }
                    </div>
                </Box>
            </div>

            <PageFooter>
                {canEdit &&
                    <div className="personalized-actions">
                        <span className={`personalized-save-state ${dirty ? 'dirty' : ''}`}>
                            {dirty
                                ? <>Alterações não salvas{status && data.status ? ' · vão direto para o site' : ''}</>
                                : <><Icon name="check" size={14} /> Tudo salvo</>}
                        </span>
                        {dirty && <Button type="secondary" size="small" text="Descartar" disabled={saving} action={() => resetForm(data)} />}
                        <Button
                            type="primary"
                            size="small"
                            svg="save-white"
                            text={saveLabel}
                            loading={saving}
                            disabled={!dirty}
                            action={save}
                        />
                    </div>
                }
            </PageFooter>

            <Modal
                className="brand-confirm-modal"
                title="Sair sem salvar?"
                show={navigationBlocker.blocked}
                close={navigationBlocker.cancel}
                footer={
                    <>
                        <Button type="secondary" size="small" text="Continuar editando" action={navigationBlocker.cancel} />
                        <Button type="primary" size="small" text="Sair sem salvar" action={navigationBlocker.proceed} />
                    </>
                }
            >
                <div className="brand-confirm danger">
                    <span className="brand-confirm-icon">
                        <Icon name="alert" size={22} />
                    </span>
                    <div>
                        <strong>As alterações deste produto ainda não foram salvas e vão se perder.</strong>
                    </div>
                </div>
            </Modal>
        </PanelTemplate>
    );
};

export default PersonalizedProduct;
